import React, { useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import StandardModal from 'modals/StandardModal';
import { SubscriptionHelper, UpgradeIncentiveHelper } from 'view/helpers';
import { closeModal } from 'data/reducers/modalReducer';
import {
	createSubscribeCheckout,
	upgradeSubscription,
	updateSmsAddon,
	fetchAvailablePlans,
} from 'data/reducers/subscriptionReducer';
import { SUBSCRIPTION_PLANS } from 'constants/subscriptions';

import {
	isFreeUserScenario,
	isCancelScenario,
} from 'utils/subscriptionPlanUtils';

// SMS Instant Alerts: $19.99/mo or $214.99/yr (10% yearly discount).
const SMS_ADDON_MONTHLY_PRICE = 19.99;
const SMS_ADDON_YEARLY_PRICE = 214.99;
const getSmsAddonAmountForInterval = (yearly) =>
	yearly ? SMS_ADDON_YEARLY_PRICE : SMS_ADDON_MONTHLY_PRICE;
const getSmsAddonMonthlyEquivalent = (yearly) => {
	const amount = yearly ? SMS_ADDON_YEARLY_PRICE / 12 : SMS_ADDON_MONTHLY_PRICE;
	return Math.round(amount * 100) / 100;
};
// Shown when the plan change succeeded but the add-on request did not, so the
// user is told what they were and were not charged for without depending on a
// toast that clears itself.
const SMS_ADDON_FAILURE_LEAD =
	'Your plan change went through, but the SMS Instant Alerts add-on could not be added:';
// This modal never offers a trial, so the withTrial slot of the pending
// checkout is always false; named so the add-on argument after it is not a
// bare pair of positional booleans.
const STORE_PENDING_CHECKOUT_WITH_TRIAL = false;

const UpgradeIncentiveModal = ({ options }) => {
	const dispatch = useDispatch();
	const history = useHistory();

	const { scenario, currentPlan } = options || {};

	const {
		availablePlans,
		isLoggedIn,
		userId,
		currentSubscription,
		hasSmsAddon,
		smsAddonError,
	} = useSelector((state) => ({
		availablePlans: state.subscription.availablePlans || [],
		isLoggedIn: !!state.user.authToken,
		userId: state.user.userData?.id,
		currentSubscription: state.subscription.currentSubscription,
		smsAddonError: state.subscription.smsAddonError,
		hasSmsAddon: Boolean(
			state.subscription?.currentSubscription?.has_sms_addon ||
			state.subscription?.currentSubscription?.subscription?.has_sms_addon ||
			state.subscription?.usageSummary?.has_sms_addon ||
			state.subscription?.usageSummary?.hasSmsAddon
		),
	}));

	const currentInterval =
		currentSubscription?.interval ||
		currentSubscription?.subscription?.interval ||
		currentSubscription?.plan?.interval ||
		currentPlan?.interval ||
		'yearly';

	const [selectedPlan, setSelectedPlan] = useState(null);
	const [isYearly, setIsYearly] = useState(currentInterval === 'yearly');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);
	const [includeSmsAddon, setIncludeSmsAddon] = useState(
		options?.withSmsAddon ?? hasSmsAddon ?? false
	);
	const seededSmsAddon = options?.withSmsAddon;

	// Once the user touches the billing toggle, picks a plan, or ticks the
	// add-on card, stop deriving those values from the subscription that keeps
	// getting refetched.
	const hasChosenInterval = useRef(false);
	const hasChosenPlan = useRef(false);
	const hasChosenSmsAddon = useRef(false);

	const hasActiveSubscription = Boolean(
		currentSubscription?.id || currentSubscription?.subscription?.id
	);

	const smsAddonSelected = includeSmsAddon;

	const chooseInterval = (yearly) => {
		hasChosenInterval.current = true;
		setErrorMessage(null);
		setIsYearly(yearly);
	};

	/** Set the SMS add-on selection. */
	const chooseSmsAddon = (selected) => {
		hasChosenSmsAddon.current = true;
		setIncludeSmsAddon(selected);
	};

	/**
	 * Apply the add-on selection after a plan change, resolving to whether the
	 * add-on now matches what the user consented to. A rejection is reported
	 * rather than thrown, because the plan change itself already succeeded.
	 */
	const reconcileSmsAddon = async () => {
		if (smsAddonSelected === hasSmsAddon) {
			return true;
		}
		try {
			await dispatch(updateSmsAddon(smsAddonSelected));
			return true;
		} catch (smsAddonFailure) {
			console.error(
				'SMS add-on update failed after upgrade:',
				smsAddonFailure,
			);
			return false;
		}
	};

	const choosePlan = (plan) => {
		hasChosenPlan.current = true;
		setErrorMessage(null);
		setSelectedPlan(plan);
	};

	// Seed isYearly once, if the current subscription loads after mount
	React.useEffect(() => {
		if (hasChosenInterval.current) return;
		const subInterval =
			currentSubscription?.interval ||
			currentSubscription?.subscription?.interval ||
			currentSubscription?.plan?.interval;
		if (subInterval) {
			setIsYearly(subInterval === 'yearly');
		}
	}, [currentSubscription]);

	// The modal can mount before the subscription resolves, so seed the add-on
	// card again when the live flag arrives; otherwise a subscriber who already
	// pays for it sees it unticked and confirming asks for its removal. An
	// explicit caller-supplied value and a choice the user has already made both
	// outrank the seed, so a late resolve can never overwrite either.
	React.useEffect(() => {
		if (hasChosenSmsAddon.current || seededSmsAddon !== undefined) {
			return;
		}
		setIncludeSmsAddon(hasSmsAddon);
	}, [hasSmsAddon, seededSmsAddon]);

	// Fetch plans on mount if not available
	React.useEffect(() => {
		if (availablePlans.length === 0) {
			dispatch(fetchAvailablePlans());
		}
	}, [dispatch, availablePlans.length]);

	// Get combined plans for display
	const combinedPlans = useMemo(() => {
		if (availablePlans.length === 0) return [];
		const allPlans =
			SubscriptionHelper.combineSubscriptionPlans(availablePlans);
		// Only show paid plans (exclude free and enterprise for incentive)
		return allPlans.filter(
			(plan) =>
				plan.id !== SUBSCRIPTION_PLANS.FREE &&
				plan.id !== SUBSCRIPTION_PLANS.ENTERPRISE,
		);
	}, [availablePlans]);

	// Filter plans based on current plan (for upgrades only)
	const filteredPlans = useMemo(() => {
		if (isFreeUserScenario(scenario) && currentPlan) {
			const currentPlanType = SubscriptionHelper.extractPlanType(
				currentPlan.name,
			);
			const currentPlanLevel =
				SubscriptionHelper.PLAN_HIERARCHY[currentPlanType] || 0;

			// Only show higher tier plans for upgrades
			return combinedPlans.filter((plan) => {
				const planLevel = SubscriptionHelper.PLAN_HIERARCHY[plan.id] || 0;
				return planLevel > currentPlanLevel;
			});
		}
		return combinedPlans; // Show all plans for free users
	}, [scenario, currentPlan, combinedPlans]);

	// Auto-select a default plan only for users without an active subscription.
	// For existing subscribers, confirming charges a prorated amount right away,
	// so the plan must be picked explicitly.
	React.useEffect(() => {
		if (hasActiveSubscription) return;
		if (!selectedPlan && filteredPlans.length > 0) {
			const defaultPlan =
				filteredPlans.find((p) => p.popular) || filteredPlans[0];
			setSelectedPlan(defaultPlan);
		}
	}, [filteredPlans, selectedPlan, hasActiveSubscription]);

	const getModalConfig = () => {
		if (isFreeUserScenario(scenario)) {
			return {
				title: "🚀 Unlock Your Property Portfolio's Full Potential",
				subtitle:
					"You're missing out on powerful features that can save you time and money",
				confirmText: 'Upgrade Now',
				confirmColor: 'success',
				benefits: [
					{
						icon: '📊',
						title: 'Unlimited Properties',
						description: 'Track as many properties as you need without limits',
					},
					{
						icon: '🔔',
						title: 'Advanced Alerts',
						description:
							'Get instant notifications from DOB, HPD, DOF, FDNY & more',
					},
					{
						icon: '👥',
						title: 'Team Collaboration',
						description: 'Add team members and share portfolios securely',
					},
					{
						icon: '📈',
						title: 'Detailed Reports',
						description: 'Export comprehensive property reports and analytics',
					},
					{
						icon: '🎯',
						title: 'Priority Support',
						description: 'Get help when you need it with dedicated support',
					},
					{
						icon: '💰',
						title: 'ROI Tracking',
						description: 'Monitor your investments and track performance',
					},
				],
			};
		} else if (isCancelScenario(scenario)) {
			return {
				title: "⏸️ Wait! Don't Miss Out on These Amazing Features",
				subtitle: "Before you cancel, see what you'll be giving up",
				confirmText: 'Stay & Upgrade',
				confirmColor: 'primary',
				benefits: [
					{
						icon: '🚨',
						title: 'Critical Property Alerts',
						description:
							'Stay ahead of violations, permits, and regulatory changes',
					},
					{
						icon: '📋',
						title: 'Compliance Tracking',
						description: 'Never miss important deadlines or requirements',
					},
					{
						icon: '📊',
						title: 'Portfolio Analytics',
						description:
							'Make data-driven decisions with comprehensive insights',
					},
					{
						icon: '🔗',
						title: 'API Integrations',
						description: 'Connect with your existing property management tools',
					},
					{
						icon: '🏆',
						title: 'Premium Features',
						description: 'Access advanced features that boost your efficiency',
					},
					{
						icon: '💼',
						title: 'Professional Support',
						description:
							'Get expert help from our property management specialists',
					},
				],
			};
		} else {
			return {
				title: 'Upgrade Your Experience',
				subtitle: 'Unlock more features with a premium plan',
				confirmText: 'Upgrade',
				confirmColor: 'primary',
				benefits: [],
			};
		}
	};

	const config = getModalConfig();

	const handleUpgrade = () => {
		// Track conversion
		if (userId) {
			UpgradeIncentiveHelper.trackUpgradeIncentiveConverted(
				scenario,
				userId,
				selectedPlan?.id || 'no_plan_selected',
			);
		}

		// Call parent onConvert if provided
		if (options.onConvert && selectedPlan) {
			options.onConvert(
				isYearly ? selectedPlan?.yearlyPlanId : selectedPlan?.monthlyPlanId,
			);
			dispatch(closeModal());
			return;
		}

		if (selectedPlan) {
			// Existing subscribers are charged immediately, so never act on a
			// plan the user has not explicitly picked.
			if (hasActiveSubscription && !hasChosenPlan.current) {
				setErrorMessage('Please choose a plan to continue.');
				return;
			}

			const targetPlanId = isYearly
				? selectedPlan?.yearlyPlanId
				: selectedPlan?.monthlyPlanId;

			if (!targetPlanId) {
				setErrorMessage(
					`${selectedPlan.name} is not available on ${
						isYearly ? 'yearly' : 'monthly'
					} billing. Please pick the other billing period or another plan.`,
				);
				return;
			}

			// Store plan selection and redirect to checkout. The add-on rides
			// along as the 4th argument so the object PricingTable replays
			// after sign-up carries the same selection this modal quoted.
			SubscriptionHelper.storePendingCheckout(
				selectedPlan,
				isYearly,
				STORE_PENDING_CHECKOUT_WITH_TRIAL,
				smsAddonSelected,
			);

			if (!isLoggedIn) {
				history.push('/sign-up?redirect=checkout');
				dispatch(closeModal());
			} else {
				setIsLoading(true);
				setError(null);
				setErrorMessage(null);

				const onFailure = (err) => {
					setIsLoading(false);
					setError(err?.message || 'Failed to complete the upgrade');
					setErrorMessage(
						err?.message ||
							'We could not complete the upgrade. Please try again or contact support.',
					);
				};

				if (hasActiveSubscription) {
					dispatch(upgradeSubscription(targetPlanId, true))
						.then(async () => {
							// The add-on request is awaited so the modal cannot close
							// over an add-on the user consented to and paid nothing
							// for. Only a settled, applied add-on closes this modal.
							const smsAddonApplied = await reconcileSmsAddon();
							setIsLoading(false);
							if (smsAddonApplied) {
								dispatch(closeModal());
							}
						})
						.catch(onFailure);
				} else {
					// createSubscribeCheckout redirects to Stripe on success; only
					// surface an error when no checkout URL comes back.
					dispatch(createSubscribeCheckout(targetPlanId, smsAddonSelected))
						.then((res) => {
							if (!res?.checkoutUrl) {
								setIsLoading(false);
								setErrorMessage(
									'We could not start the checkout session. Please try again.',
								);
							}
						})
						.catch(onFailure);
				}
			}
		} else {
			// If no plan selected, redirect to pricing page
			dispatch(closeModal());
			history.push('/pricing');
		}
	};

	const handleCancel = () => {
		// Track dismissal
		if (userId) {
			UpgradeIncentiveHelper.trackUpgradeIncentiveDismissed(scenario, userId);
		}

		// For cancel scenario, call the proceed with cancel callback
		if (isCancelScenario(scenario) && options.onProceedWithCancel) {
			options.onProceedWithCancel();
			return; // Exit early so we don't call onDismiss
		}

		// Call parent onDismiss if provided (for non-cancel scenarios or dismiss action)
		if (options.onDismiss) {
			options.onDismiss();
		}

		dispatch(closeModal());
	};

	const renderPlanSelection = () => {
		if (filteredPlans.length === 0) {
			return (
				<div className="alert alert-info">
					<p className="mb-0">Loading available plans...</p>
				</div>
			);
		}

		return (
			<div>
				{/* Billing toggle */}
				<div className="d-flex justify-content-center mb-4">
					<div className="btn-group" role="group">
						<button
							type="button"
							className={`btn btn-sm ${
								!isYearly ? 'btn-primary' : 'btn-outline-secondary'
							}`}
							onClick={() => chooseInterval(false)}
						>
							Monthly
						</button>
						<button
							type="button"
							className={`btn btn-sm ${
								isYearly ? 'btn-primary' : 'btn-outline-secondary'
							}`}
							onClick={() => chooseInterval(true)}
						>
							Yearly
							<span className="badge bg-success ms-1">Save 10%</span>
						</button>
					</div>
				</div>

				{/* Plan cards */}
				<div className="row g-3">
					{filteredPlans.map((plan) => {
						const price = SubscriptionHelper.getPlanPrice(plan, isYearly);
						const isSelected = selectedPlan?.id === plan.id;

						return (
							<div key={plan.id} className="col-12">
								<div
									className={`card h-100 cursor-pointer ${
										isSelected ? 'border-primary' : 'border-light'
									} ${plan.popular ? 'border-success' : ''}`}
									onClick={() => choosePlan(plan)}
									style={{ cursor: 'pointer' }}
								>
									{plan.popular && (
										<div className="card-header text-center bg-success text-white py-2">
											<small className="fw-bold">Most Popular</small>
										</div>
									)}
									<div className="card-body p-3">
										<div className="d-flex justify-content-between align-items-center">
											<div>
												<h6 className="card-title mb-1 fw-bold">{plan.name}</h6>
												<p className="text-muted small mb-0">{plan.subtitle}</p>
											</div>
											<div className="text-end">
												<div className="h5 mb-0 text-primary fw-bold">
													${price}
													<small className="text-muted fw-normal">/mo</small>
												</div>
												{isYearly && (
													<small className="text-success">Billed yearly</small>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>

				{/* SMS Add-on Selection */}
				<div className="mt-3">
					<div
						className={`card p-3 cursor-pointer ${
							smsAddonSelected
								? 'border-success bg-white shadow-sm'
								: 'border-light bg-light'
						}`}
						style={{
							cursor: 'pointer',
							transition: 'all 0.2s ease',
							borderWidth: smsAddonSelected ? '2px' : '1px',
						}}
						onClick={() => chooseSmsAddon(!smsAddonSelected)}
					>
						<div className="d-flex justify-content-between align-items-center">
							<div className="d-flex align-items-center pr-2">
								<div className="mr-3" style={{ fontSize: '1.4rem', lineHeight: 1 }}>
									📱
								</div>
								<div>
									<div className="d-flex align-items-center">
										<span
											className="font-weight-bold"
											style={{ fontSize: '0.92rem', color: '#111827' }}
										>
											SMS Instant Alerts
										</span>
										<span
											className="badge bg-success ms-2 text-white"
											style={{ fontSize: '0.75rem' }}
										>
											+${getSmsAddonAmountForInterval(isYearly).toFixed(2)}
											{isYearly ? '/yr' : '/mo'}
										</span>
									</div>
									<small className="text-muted d-block mt-1">
										Instant real-time SMS for Stop Work Orders, ECB/DOB hearings
										and critical deadlines
									</small>
								</div>
							</div>
							<div className="form-check m-0">
								<input
									type="checkbox"
									className="form-check-input"
									checked={smsAddonSelected}
									onChange={(e) => chooseSmsAddon(e.target.checked)}
									style={{
										cursor: 'pointer',
										width: '1.2rem',
										height: '1.2rem',
									}}
								/>
							</div>
						</div>
					</div>
				</div>

				{errorMessage && (
					<div className="mt-3">
						<div className="alert alert-danger mb-0" role="alert">
							{errorMessage}
						</div>
					</div>
				)}

				{selectedPlan && (
					<div className="mt-3">
						<div className="alert alert-success mb-0">
							<div className="d-flex justify-content-between align-items-center">
								<div>
									<strong>Selected Plan:</strong> {selectedPlan.name}
									{smsAddonSelected && (
										<span className="text-success ms-1">+ SMS Add-on</span>
									)}
								</div>
								<div className="text-end">
									<strong>
										$
										{(
											parseFloat(
												SubscriptionHelper.getPlanPrice(
													selectedPlan,
													isYearly,
												),
											) +
											(smsAddonSelected
												? getSmsAddonMonthlyEquivalent(isYearly)
												: 0)
										).toFixed(2)}
										/mo
									</strong>
									{isYearly ? (
										<small className="d-block text-success">
											Billed yearly
											{smsAddonSelected
												? ` + $${SMS_ADDON_YEARLY_PRICE.toFixed(2)}/yr SMS`
												: ''}
										</small>
									) : (
										<small className="d-block text-muted">Billed monthly</small>
									)}
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderBenefits = () => {
		return (
			<div className="row g-3 mb-4">
				{config.benefits.map((benefit, index) => (
					<div key={index} className="col-12 col-md-6">
						<div className="d-flex align-items-start">
							<div className="me-3" style={{ fontSize: '1.5rem' }}>
								{benefit.icon}
							</div>
							<div>
								<h6 className="mb-1 fw-bold">{benefit.title}</h6>
								<p className="text-muted mb-0 small">{benefit.description}</p>
							</div>
						</div>
					</div>
				))}
			</div>
		);
	};

	return (
		<StandardModal
			title={config.title}
			size="lg"
			closeOnOutsideClick
			confirmText={config.confirmText}
			confirmColor={config.confirmColor}
			closeLabel={
				isCancelScenario(scenario) ? 'Proceed with Cancellation' : 'Maybe Later'
			}
			onConfirm={handleUpgrade}
			onClose={() => {
				if (options.onDismiss) {
					options.onDismiss();
				}
			}}
			isLoading={isLoading}
			onCancel={handleCancel}
			disableConfirm={hasActiveSubscription && !selectedPlan}
		>
			<div>
				{error && (
					<div className="alert alert-danger mb-3" role="alert">
						<i className="bi bi-exclamation-triangle-fill me-2" />
						{error}
					</div>
				)}

				{smsAddonError && (
					<div className="alert alert-warning mb-3" role="alert">
						<i className="bi bi-exclamation-triangle-fill me-2" />
						<strong>{SMS_ADDON_FAILURE_LEAD}</strong>{' '}
						<span>{smsAddonError}</span>
					</div>
				)}

				{/* Subtitle */}
				<div className="text-center mb-4">
					<p className="text-muted lead">{config.subtitle}</p>
				</div>

				{/* Benefits */}
				{renderBenefits()}

				{/* Plan Selection */}
				<div className="border-top pt-4">
					<h5 className="text-center mb-4">Choose Your Plan</h5>
					{renderPlanSelection()}
				</div>

				{/* Special offer for cancel users */}
				{isCancelScenario(scenario) && false && (
					<div className="mt-4">
						<div className="alert alert-warning">
							<div className="d-flex align-items-center">
								<i className="bi bi-gift me-2"></i>
								<div>
									<strong>Special Offer:</strong> Upgrade now and get your first
									month at 25% off!
									<small className="d-block text-muted">
										Limited time offer for valued customers
									</small>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Trust indicators */}
				<div className="text-center mt-4">
					<div className="d-flex justify-content-center align-items-center text-muted small">
						<i className="bi bi-credit-card me-2"></i>
						<span className="me-3">Secure payment</span>
						<i className="bi bi-arrow-repeat me-2"></i>
						<span>Cancel anytime</span>
					</div>
				</div>
			</div>
		</StandardModal>
	);
};

export default UpgradeIncentiveModal;
